/**
 * PSP/CRE TOOL v2.0 - NATO TRIAGE CATEGORIES
 * 
 * @fileoverview NATO 5-category triage system with distribution, survival curves,
 *               resource requirements, and triage officer performance modeling
 * @module constants/triageCategories
 * @author Dr. Tatar Radu MD - Romanian Military Medical Institute
 * @date December 20, 2024
 * @version 2.0 - Enhanced with re-triage, CBRN, START/SALT algorithms
 * 
 * Changes from v1.0:
 * - Added RE_TRIAGE_PROTOCOL for category transitions
 * - Added START_TRIAGE_ALGORITHM (Simple Triage and Rapid Treatment)
 * - Added SALT_TRIAGE_ALGORITHM (Sort, Assess, Lifesaving, Treatment)
 * - Added CBRN_TRIAGE_MODIFICATIONS
 * - Added BURN_TRIAGE_CRITERIA
 * - Added PEDIATRIC_TRIAGE_MODIFICATIONS
 * - Added category transition tracking
 * - Added triage documentation requirements
 * - Enhanced calculator integration
 * 
 * DATA SOURCES:
 * - CASUALTY_CALCULATOR_ARCHITECTURE_PART1_NOV24_2025.md (lines 498-650)
 * - NATO STANAG 2879 (Triage Categories)
 * - AJMedP-1 Chapter 3 (Medical Planning)
 * - Ukraine 2022-2024 operational data (triage accuracy under stress)
 * 
 * PURPOSE:
 * Educational tool for 19-year-old Romanian military medical students
 * Learning NATO triage protocols, resource allocation, and time-critical decision making
 * 
 * CRITICAL CONCEPTS FOR STUDENTS:
 * 1. Triage is dynamic - categories change based on available resources
 * 2. Time windows for T1 casualties are measured in MINUTES not hours
 * 3. Triage officer fatigue significantly degrades accuracy (95% â†’ 55%)
 * 4. Mass casualty events require additional triage officers
 * 5. T4 (Expectant) designation is resource-driven, not medical death sentence
 */

// ============================================================================
// NATO 5-CATEGORY TRIAGE SYSTEM (STANAG 2879)
// ============================================================================

/**
 * NATO triage categories with detailed medical specifications
 * 
 * EDUCATIONAL NOTE:
 * These categories form the foundation of NATO military medical doctrine.
 * Understanding when a patient moves from T2 to T1 (as condition deteriorates)
 * or T1 to T4 (when resources exhausted) is critical for medical planning.
 * 
 * ROMANIAN MILITARY CONTEXT:
 * Romanian Army follows NATO standards. Most likely scenario: CONTESTED
 * environment (Eastern European peer conflict) where golden hour rarely achieved.
 * T2 and PFC capabilities become MORE important than T1 surgical focus.
 */
export const TRIAGE_CATEGORIES = {
  /**
   * T1 - IMMEDIATE (Red Tag)
   * Life-threatening injuries requiring immediate surgical intervention
   * 
   * TIME WINDOW: <2 hours (preferably <1 hour)
   * SURVIVAL: 92% if surgery <60min, drops to 35% by 2 hours
   * 
   * TYPICAL INJURIES:
   * - Major hemorrhage not controlled by tourniquet (junctional wounds)
   * - Tension pneumothorax requiring chest decompression
   * - Airway obstruction with respiratory distress
   * - Traumatic amputation with ongoing bleeding
   * - Open chest/abdominal wounds with active hemorrhage
   * - Severe blast lung (if resources available)
   * 
   * UKRAINIAN REALITY (2022-2024):
   * Only 38% of T1 casualties reach surgery within 60 minutes in contested
   * environments. This drives the need for Prolonged Field Care (PFC) and
   * damage control resuscitation at forward positions.
   */
  T1_IMMEDIATE: {
    code: 'T1',
    name: 'IMMEDIATE',
    color: 'RED',
    nato_priority: 1,
    
    // Distribution percentages
    baseline_percentage: 0.25,      // 25% of WIA in balanced operations
    attack_percentage: 0.28,        // Higher in offensive operations
    defense_percentage: 0.22,       // Lower in defensive operations
    
    // Time windows (minutes)
    optimal_surgery_time: 60,       // "Golden Hour" window
    acceptable_surgery_time: 120,   // Still survivable window
    critical_threshold: 240,        // Beyond this: very poor outcomes
    
    // Survival probabilities by time to surgery
    survival_curves: {
      under_60min: 0.92,            // 92% survival with immediate surgery
      between_60_120min: 0.68,      // 68% survival with 1-2h delay
      between_120_240min: 0.35,     // 35% survival with 2-4h delay
      over_240min: 0.12,            // 12% survival beyond 4 hours
      no_surgery: 0.10              // 10% survive without intervention
    },
    
    // Resource requirements
    resources: {
      surgical_team_required: true,
      surgery_duration_minutes: 120, // Average T1 surgery time
      icu_required: true,
      icu_days: 3,                  // Average ICU stay post-op
      
      // Blood product consumption (massive transfusion typical)
      blood_products: {
        prbc_units: 12,             // Packed Red Blood Cells (avg)
        ffp_units: 12,              // Fresh Frozen Plasma (1:1 ratio)
        platelet_units: 2,          // Platelet packs
        transfusion_protocol: 'MASSIVE', // MTP threshold: >10 units
        whole_blood_alternative: true    // Walking blood bank option
      },
      
      // Medical supplies per T1 patient
      supplies: {
        iv_fluids_liters: 4,
        hemostatic_dressings: 3,
        chest_tubes: 1,
        txa_doses: 2,               // Tranexamic acid
        antibiotics_doses: 4
      }
    },
    
    // Facility requirements
    treatment_facilities: {
      role1_stabilization: true,     // Initial resuscitation only
      role2_basic_inadequate: true,  // Cannot provide surgery
      role2_enhanced_minimum: true,  // Can do DCS Phase 1
      role3_optimal: true,           // Full DCS capability
      role4_definitive: true         // Reconstructive surgery later
    },
    
    // Evacuation priority
    evacuation: {
      priority: 'URGENT',           // Highest evacuation priority
      helicopter_preferred: true,
      ground_acceptable: false,     // Too slow for T1
      max_evacuation_time: 60       // Minutes before outcomes worsen
    }
  },

  /**
   * T2 - DELAYED (Yellow Tag)
   * Serious injuries that can tolerate surgical delay
   * 
   * TIME WINDOW: 6-24 hours acceptable
   * SURVIVAL: 96% if treated <6h, 91% if 6-24h, 87% if 24-72h
   * 
   * TYPICAL INJURIES:
   * - Closed long bone fractures (femur, tibia)
   * - Soft tissue wounds without major hemorrhage
   * - Maxillofacial injuries without airway compromise
   * - Eye injuries (globe rupture, penetrating trauma)
   * - Genitourinary injuries (stable)
   * - Burns 20-40% TBSA without airway involvement
   * 
   * CRITICAL TEACHING POINT:
   * T2 patients are the "swing category" - they can deteriorate to T1 if not
   * monitored. In contested environments, T2 patients often spend 12-24 hours
   * at Role 2 facilities before evacuation to Role 3. Proper stabilization
   * and monitoring during this hold time is essential.
   */
  T2_DELAYED: {
    code: 'T2',
    name: 'DELAYED',
    color: 'YELLOW',
    nato_priority: 2,
    
    // Distribution percentages
    baseline_percentage: 0.40,      // 40% of WIA (largest category)
    attack_percentage: 0.38,
    defense_percentage: 0.42,
    
    // Time windows (hours)
    optimal_surgery_time: 6,        // Best outcomes
    acceptable_surgery_time: 24,    // Still good outcomes
    delayed_surgery_time: 72,       // Acceptable in resource constraints
    
    // Survival probabilities by time to surgery
    survival_curves: {
      under_6hours: 0.96,           // 96% survival with prompt treatment
      between_6_24hours: 0.91,      // 91% survival with reasonable delay
      between_24_72hours: 0.87,     // 87% survival with extended delay
      over_72hours: 0.78,           // 78% survival beyond 3 days
      complications_risk: 0.15      // 15% develop complications if delayed
    },
    
    // Resource requirements
    resources: {
      surgical_team_required: true,
      surgery_duration_minutes: 90,  // Average T2 surgery time
      icu_required: false,           // Usually post-op ward sufficient
      ward_days: 5,                  // Average ward stay
      
      // Blood product consumption (moderate)
      blood_products: {
        prbc_units: 4,              // Moderate transfusion typical
        ffp_units: 2,
        platelet_units: 0,          // Rarely needed
        transfusion_protocol: 'STANDARD'
      },
      
      // Medical supplies per T2 patient
      supplies: {
        iv_fluids_liters: 2,
        antibiotics_doses: 8,       // Longer course for wound infection prevention
        pain_medication_days: 7,
        dressings: 5
      }
    },
    
    // Facility requirements
    treatment_facilities: {
      role1_initial_treatment: true,  // Can provide initial stabilization
      role2_basic_hold: true,         // Can hold 12-24h with monitoring
      role2_enhanced_surgery: true,   // Can perform most T2 surgeries
      role3_optimal: true,
      role4_definitive: true
    },
    
    // Evacuation priority
    evacuation: {
      priority: 'PRIORITY',          // Second priority after T1
      helicopter_preferred: true,
      ground_acceptable: true,       // Can tolerate ground transport
      max_hold_time_hours: 24        // Can safely wait at Role 2
    },
    
    // Monitoring requirements during hold time
    monitoring: {
      vital_signs_frequency_hours: 2, // Q2h vital signs
      reassessment_hours: 4,          // Reassess triage category Q4h
      deterioration_signs: [
        'increasing_pain',
        'hemodynamic_instability',
        'compartment_syndrome',
        'wound_infection'
      ]
    }
  },

  /**
   * T3 - MINIMAL (Green Tag)
   * Minor injuries, "walking wounded"
   * 
   * TIME WINDOW: 24-72 hours acceptable for treatment
   * RTD RATE: 70% return to duty within 7 days
   * 
   * TYPICAL INJURIES:
   * - Minor lacerations and abrasions
   * - Minor fractures (fingers, toes, ribs)
   * - Sprains and strains
   * - Minor burns (<10% TBSA, no airway)
   * - Superficial fragmentation wounds
   * - Concussion without LOC
   * 
   * OPERATIONAL IMPORTANCE:
   * T3 patients are critical for maintaining fighting strength. 70% return to
   * duty within 7 days if properly treated. In Ukrainian operations, many T3
   * patients remained with units (treated at Role 1) rather than evacuating,
   * preserving limited evacuation assets for T1/T2 casualties.
   */
  T3_MINIMAL: {
    code: 'T3',
    name: 'MINIMAL',
    color: 'GREEN',
    nato_priority: 3,
    
    // Distribution percentages
    baseline_percentage: 0.30,      // 30% of WIA
    // CALC-010: moved here from a literal inside survivalModule (value unchanged, 2026-10-09).
    // Share of T3 casualties who die of wounds. Source still to be confirmed.
    mortality_rate: 0.02,
    attack_percentage: 0.29,
    defense_percentage: 0.32,
    
    // Time windows
    treatment_window_hours: 72,     // Can wait up to 3 days
    
    // Return to Duty (RTD) rates
    rtd_rates: {
      within_24hours: 0.20,         // 20% RTD same day (very minor)
      within_72hours: 0.45,         // 45% RTD within 3 days
      within_7days: 0.70,           // 70% RTD within 1 week
      medical_evacuation: 0.30      // 30% require evacuation rear
    },
    
    // Resource requirements (minimal)
    resources: {
      surgical_team_required: false, // No surgery needed
      treatment_duration_minutes: 20, // Quick treatment
      hospitalization_required: false, // Outpatient treatment
      
      blood_products: {
        transfusion_required: false  // No blood products needed
      },
      
      // Medical supplies per T3 patient
      supplies: {
        dressings: 2,
        antibiotics_doses: 3,        // Prophylactic course
        pain_medication_days: 3,
        splints: 0.3                 // 30% need splinting
      }
    },
    
    // Facility requirements
    treatment_facilities: {
      role1_sufficient: true,        // Can be fully treated at Role 1
      role2_basic_if_needed: true,
      evacuation_not_required: true  // Most stay with unit
    },
    
    // Evacuation priority
    evacuation: {
      priority: 'ROUTINE',           // Lowest evacuation priority
      helicopter_not_justified: true, // Ground transport if evacuated
      ground_acceptable: true,
      delay_acceptable_hours: 48     // Can wait 2 days for transport
    },
    
    // Unit retention
    unit_retention: {
      keep_with_unit: true,          // Doctrine: treat and return
      light_duty_days: 3,            // Average light duty period
      preserves_unit_strength: true  // Critical for operational tempo
    }
  },

  /**
   * T4 - EXPECTANT (Blue/Black Tag)
   * Injuries currently incompatible with survival given available resources
   * 
   * SURVIVAL: 2% even with treatment
   * ETHICAL DILEMMA: Resource allocation vs individual patient care
   * 
   * TYPICAL INJURIES:
   * - Massive head trauma (GCS 3-4, fixed pupils)
   * - Burns >80% TBSA
   * - Multiple system trauma with hemodynamic collapse
   * - Severe blast lung + other major injuries
   * - Injuries requiring >6 hours surgery (when many T1 waiting)
   * 
   * CRITICAL TEACHING:
   * T4 is NOT a medical death sentence - it's a RESOURCE-DRIVEN decision.
   * Same patient might be T1 at Role 4 hospital (unlimited resources) but
   * T4 at Role 2 during MASCAL (overwhelming casualties). This is the hardest
   * decision medical personnel make. T4 patients receive pain management and
   * comfort measures - they are NOT abandoned.
   */
  T4_EXPECTANT: {
    code: 'T4',
    name: 'EXPECTANT',
    color: 'BLUE',        // Some nations use BLACK
    nato_priority: 4,
    
    // Distribution percentages
    baseline_percentage: 0.05,      // 5% of WIA
    // CALC-010: moved here from a literal inside survivalModule (value unchanged, 2026-10-09).
    // Share of T4 (expectant) casualties who die of wounds. Source still to be confirmed.
    mortality_rate: 0.90,
    mass_casualty_percentage: 0.08, // Higher during MASCAL
    
    // Survival (grim prognosis)
    survival_with_treatment: 0.02,  // 2% survive even with full care
    survival_without_treatment: 0.0, // Effectively zero
    
    // Resource considerations
    resources: {
      surgical_team_ineffective: true, // Would consume hours with poor outcome
      blood_products_ineffective: true, // Would deplete blood bank
      
      // Palliative care provided
      comfort_measures: {
        pain_management: true,        // Opioid pain control
        oxygen_if_available: true,
        companionship: true,          // Someone stays with patient
        dignity_maintained: true
      }
    },
    
    // When T4 designation appropriate
    designation_criteria: {
      during_mass_casualty: true,    // Many T1 patients waiting
      limited_resources: true,       // Blood/supplies critically low
      survival_probability_under: 0.05, // <5% survival chance
      treatment_time_over_hours: 6   // Would take >6h surgery
    },
    
    // Ethical framework
    ethical_considerations: {
      greatest_good: true,           // Utilitarian principle
      reversible_decision: true,     // Can upgrade if resources improve
      cultural_sensitivity: true,    // Religious/cultural considerations
      documentation_required: true,  // Senior medical officer decision
      family_notification: true      // If possible in combat conditions
    },
    
    // Care location
    care_location: {
      separate_area: true,           // Quiet area away from active triage
      chaplain_access: true,         // Spiritual support if available
      morphine_available: true       // Pain control priority
    }
  }
};

// ============================================================================
// START TRIAGE ALGORITHM (Simple Triage and Rapid Treatment)
// ============================================================================

/**
 * START Triage - Rapid mass casualty triage algorithm
 * Used when >20 casualties present and rapid sorting needed
 * Decision time: 30-60 seconds per patient
 */
export const START_TRIAGE_ALGORITHM = {
  name: 'START - Simple Triage and Rapid Treatment',
  time_per_patient_seconds: 45,
  appropriate_for: 'MASS_CASUALTY',
  
  steps: {
    STEP_1_WALKING: {
      action: 'Can the patient walk?',
      if_yes: 'T3_MINOR',
      if_no: 'CONTINUE_TO_STEP_2',
      notes: 'All walking wounded = T3 (Green)'
    },
    
    STEP_2_BREATHING: {
      action: 'Is the patient breathing?',
      if_no: {
        action: 'Open airway (head-tilt/chin-lift)',
        if_breathing_now: 'T1_IMMEDIATE',
        if_still_not_breathing: 'T4_EXPECTANT'
      },
      if_yes: 'CHECK_RESPIRATORY_RATE',
      notes: 'One attempt to open airway only'
    },
    
    STEP_3_RESPIRATORY_RATE: {
      action: 'Count respiratory rate',
      if_over_30: 'T1_IMMEDIATE',
      if_under_30: 'CONTINUE_TO_STEP_4',
      notes: 'RR >30 indicates shock or respiratory distress'
    },
    
    STEP_4_PERFUSION: {
      action: 'Check radial pulse OR capillary refill',
      if_no_pulse: 'T1_IMMEDIATE',
      if_cap_refill_over_2s: 'T1_IMMEDIATE',
      if_normal: 'CONTINUE_TO_STEP_5',
      notes: 'No radial pulse = SBP <80mmHg'
    },
    
    STEP_5_MENTAL_STATUS: {
      action: 'Can patient follow simple commands?',
      if_no: 'T1_IMMEDIATE',
      if_yes: 'T2_DELAYED',
      notes: 'Altered mental status with normal vitals = T1'
    }
  },
  
  accuracy: {
    sensitivity_for_critical: 0.85,
    specificity: 0.70,
    overtriage_rate: 0.30,
    undertriage_rate: 0.05
  }
};

/**
 * SALT Triage - More refined mass casualty algorithm
 * Sort, Assess, Lifesaving interventions, Treatment/Transport
 */
export const SALT_TRIAGE_ALGORITHM = {
  name: 'SALT - Sort, Assess, Lifesaving, Treatment/Transport',
  time_per_patient_seconds: 60,
  appropriate_for: 'MASS_CASUALTY',
  
  steps: {
    SORT: {
      action: 'Global sorting by voice command',
      wave_1: 'Walk to designated area → T3 (Minor)',
      wave_2: 'Wave or follow commands → Assess second',
      wave_3: 'No response/movement → Assess first',
      notes: 'Rapidly sorts into groups for individual assessment'
    },
    
    ASSESS: {
      action: 'Individual assessment of non-walking',
      check: ['Breathing', 'Major hemorrhage', 'Shock signs'],
      duration_seconds: 30
    },
    
    LIFESAVING: {
      action: 'Perform only these interventions',
      interventions: [
        'Control major hemorrhage (tourniquet/pressure)',
        'Open airway (positioning only)',
        'Chest decompression if tension pneumothorax',
        'Auto-injector antidotes (nerve agent)'
      ],
      time_limit_seconds: 60,
      notes: 'Only interventions that can be done in <60 seconds'
    },
    
    TREATMENT_TRANSPORT: {
      action: 'Assign category and move',
      dead: 'No pulse after lifesaving → DEAD (Black)',
      expectant: 'Unlikely to survive given resources → T4 (Blue)',
      immediate: 'Life threat, likely to survive → T1 (Red)',
      delayed: 'Serious but can wait → T2 (Yellow)',
      minor: 'Walking wounded → T3 (Green)'
    }
  },
  
  accuracy: {
    sensitivity_for_critical: 0.88,
    specificity: 0.75,
    overtriage_rate: 0.25,
    undertriage_rate: 0.03
  }
};

// ============================================================================
// RE-TRIAGE PROTOCOL
// ============================================================================

/**
 * Re-triage protocol for patients whose condition changes
 * Critical for prolonged field care and holding at Role 2
 */
export const RE_TRIAGE_PROTOCOL = {
  description: 'Systematic re-evaluation of triage category over time',
  
  schedules: {
    T1_IMMEDIATE: {
      initial_reassess: 15,     // minutes
      ongoing_reassess: 30,     // minutes
      notes: 'Frequent reassessment - condition changes rapidly'
    },
    T2_DELAYED: {
      initial_reassess: 60,     // minutes
      ongoing_reassess: 120,    // minutes (2 hours)
      notes: 'Watch for deterioration to T1'
    },
    T3_MINIMAL: {
      initial_reassess: 240,    // minutes (4 hours)
      ongoing_reassess: 480,    // minutes (8 hours)
      notes: 'Less frequent - stable patients'
    },
    T4_EXPECTANT: {
      initial_reassess: 60,     // minutes
      ongoing_reassess: 60,     // minutes
      notes: 'Reassess if resources become available'
    }
  },
  
  // Category upgrade triggers (patient getting worse)
  upgrade_triggers: {
    T2_TO_T1: [
      'Hemodynamic instability (SBP <90)',
      'Respiratory distress (RR >30 or <10)',
      'Altered mental status (GCS drop >2)',
      'Uncontrolled hemorrhage',
      'Compartment syndrome signs',
      'Sepsis indicators'
    ],
    T3_TO_T2: [
      'Wound infection signs',
      'Increasing pain requiring IV opioids',
      'Fracture complications',
      'Unable to ambulate',
      'Fever >38.5°C'
    ],
    T3_TO_T1: [
      'Missed internal injury',
      'Delayed hemorrhage',
      'Pulmonary embolism',
      'Fat embolism syndrome'
    ]
  },
  
  // Category downgrade triggers (patient stabilizing or resources exhausted)
  downgrade_triggers: {
    T1_TO_T2: [
      'Hemorrhage controlled, stable vitals',
      'Airway secured, good oxygenation',
      'Surgery completed, post-op stable'
    ],
    T1_TO_T4: [
      'MASCAL declared, resources exhausted',
      'Survival probability reassessed <5%',
      'Treatment would consume disproportionate resources'
    ],
    T2_TO_T3: [
      'Wound clean, no surgery needed',
      'Pain controlled with oral meds',
      'Able to ambulate'
    ]
  },
  
  // Documentation requirements
  documentation: {
    required_fields: [
      'Time of re-triage',
      'Previous category',
      'New category',
      'Reason for change',
      'Triaging officer name/rank',
      'Vital signs at re-triage'
    ],
    form: 'DD Form 1380 (Field Medical Card)',
    electronic: 'TC3 tracking system if available'
  },
  
  ukraine_lessons: {
    observation: 'Many T2 patients deteriorated to T1 during prolonged holds',
    rate: 0.15,  // 15% of T2 upgraded to T1 within 24h
    cause: 'Delayed hemorrhage, missed injuries, infection',
    prevention: 'Structured re-assessment protocol every 2 hours'
  }
};

// ============================================================================
// CBRN TRIAGE MODIFICATIONS
// ============================================================================

/**
 * Chemical, Biological, Radiological, Nuclear triage modifications
 * Standard triage categories apply but with modifications
 */
export const CBRN_TRIAGE_MODIFICATIONS = {
  description: 'Triage modifications for CBRN mass casualty events',
  
  CHEMICAL: {
    nerve_agent: {
      critical_window_minutes: 5,
      antidote_priority: 'T1_FIRST',
      decontamination: 'IMMEDIATE',
      triage_modification: {
        apneic_with_antidote: 'T1',  // Give chance with atropine
        convulsing: 'T1',
        miosis_mild: 'T2',
        exposure_only: 'T3'
      },
      notes: 'Antidote can reverse even severe exposure if given quickly'
    },
    
    blister_agent: {
      triage_modification: {
        burns_over_50_percent: 'T4',
        burns_20_50_percent: 'T2',
        burns_under_20_percent: 'T3',
        eye_exposure: 'T2'
      },
      decontamination_window: 15,  // minutes
      notes: 'Eye irrigation critical within 15 minutes'
    },
    
    blood_agent: {
      triage_modification: {
        apneic: 'T4',  // Usually fatal
        convulsing: 'T2',
        symptomatic: 'T2'
      },
      notes: 'Hydroxocobalamin antidote if available'
    }
  },
  
  RADIOLOGICAL: {
    whole_body_dose: {
      over_8_Gy: { category: 'T4', survival: 0 },
      '6_8_Gy': { category: 'T2', survival: 0.10 },
      '4_6_Gy': { category: 'T2', survival: 0.50 },
      '2_4_Gy': { category: 'T3', survival: 0.90 },
      under_2_Gy: { category: 'T3', survival: 0.99 }
    },
    
    lymphocyte_triage: {
      description: 'Use lymphocyte count at 48h to estimate dose',
      over_1200: 'Mild exposure (<2 Gy)',
      '800_1200': 'Moderate (2-4 Gy)',
      '300_800': 'Severe (4-6 Gy)',
      under_300: 'Lethal (>6 Gy)'
    },
    
    combined_injury: {
      radiation_plus_trauma: 'Increase severity by one category',
      radiation_plus_burn: 'Increase severity by one category',
      notes: 'Combined injuries have synergistic mortality'
    }
  },
  
  BIOLOGICAL: {
    triage_considerations: [
      'Isolate suspected cases immediately',
      'PPE for all medical personnel',
      'Decontamination before treatment',
      'Contact tracing critical'
    ],
    
    contagious_agents: {
      ebola: { isolation: 'STRICT', ppe: 'LEVEL_A' },
      smallpox: { isolation: 'STRICT', ppe: 'N95_GOWN' },
      anthrax_cutaneous: { isolation: 'STANDARD', ppe: 'GLOVES_GOWN' }
    }
  },
  
  general_principles: [
    'DECONTAMINATE before detailed triage',
    'Protect medical personnel (they cannot help if contaminated)',
    'Establish hot/warm/cold zones',
    'Separate contaminated and clean casualties',
    'Modified triage criteria may apply'
  ]
};

// ============================================================================
// BURN TRIAGE CRITERIA
// ============================================================================

/**
 * Burns-specific triage criteria
 * Burns require specialized assessment beyond standard trauma
 */
export const BURN_TRIAGE_CRITERIA = {
  description: 'Triage criteria specifically for burn casualties',
  
  // TBSA-based triage (Total Body Surface Area)
  tbsa_categories: {
    MILITARY_CONTEXT: {
      T1_IMMEDIATE: {
        tbsa: '20-60%',
        conditions: [
          'Inhalation injury present',
          'Circumferential extremity burns',
          'Burns to face/hands/feet/perineum',
          'Electrical burns'
        ],
        notes: 'Requires immediate fluid resuscitation and airway management'
      },
      
      T2_DELAYED: {
        tbsa: '10-20%',
        conditions: [
          'No inhalation injury',
          'No circumferential burns',
          'Stable airway'
        ],
        notes: 'Can tolerate delay for fluid resuscitation'
      },
      
      T3_MINIMAL: {
        tbsa: '<10%',
        conditions: [
          'Superficial/partial thickness',
          'No critical areas involved'
        ],
        notes: 'Wound care and pain management only'
      },
      
      T4_EXPECTANT: {
        tbsa: '>80%',
        conditions: [
          'Or >60% with inhalation injury',
          'Or >70% with age >60'
        ],
        survival_rate: 0.02,
        notes: 'Survival unlikely even with full burn center resources'
      }
    }
  },
  
  // Inhalation injury indicators
  inhalation_injury: {
    indicators: [
      'Singed nasal hairs',
      'Carbonaceous sputum',
      'Hoarse voice',
      'Stridor',
      'Burns to face/neck',
      'Enclosed space fire'
    ],
    action: 'Early intubation - do not wait for swelling',
    category_modifier: 'Upgrade one category if present'
  },
  
  // Fluid resuscitation (Parkland formula)
  fluid_resuscitation: {
    formula: '4 mL × weight(kg) × %TBSA',
    first_8h: 0.50,  // 50% of total in first 8 hours
    next_16h: 0.50,  // 50% in next 16 hours
    titrate_to: 'Urine output 0.5-1 mL/kg/hr',
    notes: 'Start within 1 hour of burn for best outcomes'
  },
  
  // Resource requirements
  resource_intensive: {
    icu_days_per_percent_tbsa: 1,  // Rough estimate
    surgery_sessions: 'TBSA / 10',  // Approximate debridement surgeries
    blood_units_per_surgery: 4,
    skin_graft_needed: true
  }
};

// ============================================================================
// PEDIATRIC TRIAGE MODIFICATIONS
// ============================================================================

/**
 * Pediatric triage modifications
 * Children have different physiological parameters
 */
export const PEDIATRIC_TRIAGE_MODIFICATIONS = {
  description: 'Modified triage parameters for pediatric casualties',
  
  // JumpSTART algorithm for children
  jump_start: {
    name: 'JumpSTART Pediatric MCI Triage',
    age_range: '1-8 years',
    
    modifications: {
      walking: 'Same as adult START',
      breathing: {
        if_not_breathing: 'Give 5 rescue breaths THEN assess',
        if_still_not_breathing: 'T4 Expectant',
        if_breathing_after_rescue: 'T1 Immediate'
      },
      respiratory_rate: {
        too_fast: '>45 for infants, >30 for children',
        too_slow: '<15',
        either_extreme: 'T1 Immediate'
      },
      perfusion: {
        check: 'Peripheral pulse (brachial in infants)',
        cap_refill: '>2 seconds = T1'
      },
      mental_status: {
        inappropriate: 'AVPU = P or U → T1',
        appropriate: 'A or V → T2'
      }
    }
  },
  
  // Age-based vital sign thresholds
  vital_signs: {
    INFANT_0_1: {
      normal_rr: { min: 30, max: 50 },
      normal_hr: { min: 100, max: 160 },
      normal_sbp: { min: 70, max: 90 },
      concerning_rr: { over: 60, under: 20 },
      concerning_hr: { over: 180, under: 80 }
    },
    TODDLER_1_3: {
      normal_rr: { min: 24, max: 40 },
      normal_hr: { min: 90, max: 150 },
      normal_sbp: { min: 80, max: 100 },
      concerning_rr: { over: 50, under: 15 },
      concerning_hr: { over: 160, under: 70 }
    },
    CHILD_3_8: {
      normal_rr: { min: 20, max: 30 },
      normal_hr: { min: 80, max: 120 },
      normal_sbp: { min: 85, max: 110 },
      concerning_rr: { over: 40, under: 12 },
      concerning_hr: { over: 140, under: 60 }
    },
    CHILD_8_12: {
      normal_rr: { min: 16, max: 24 },
      normal_hr: { min: 70, max: 110 },
      normal_sbp: { min: 90, max: 120 },
      concerning_rr: { over: 30, under: 10 },
      concerning_hr: { over: 130, under: 55 }
    }
  },
  
  // Special considerations
  special_considerations: [
    'Children compensate longer then crash suddenly',
    'Hypotension is LATE sign of shock in children',
    'Tachycardia is EARLY sign - do not ignore',
    'Weight-based medication dosing critical',
    'Hypothermia risk higher - keep warm',
    'Emotional needs - keep with parent if possible'
  ],
  
  // Blood volume and fluid resuscitation
  fluid_resuscitation: {
    blood_volume_ml_per_kg: 80,  // vs 70 in adults
    initial_bolus: '20 mL/kg crystalloid',
    reassess_after_each_bolus: true,
    blood_transfusion_trigger: 'SBP <(70 + 2×age) mmHg'
  }
};

// ============================================================================
// TRIAGE DOCUMENTATION
// ============================================================================

/**
 * Triage documentation requirements
 */
export const TRIAGE_DOCUMENTATION = {
  triage_tag_fields: [
    'Name/ID (if known)',
    'Date/Time of injury',
    'Date/Time of triage',
    'Triage category',
    'Mechanism of injury',
    'Vital signs at triage',
    'Injuries identified',
    'Interventions performed',
    'Triage officer ID',
    'Decontamination status (if CBRN)',
    'Allergies (if known)',
    'Last tetanus (if known)'
  ],
  
  color_coding: {
    T1: { color: 'RED', label: 'IMMEDIATE', tape: 'Red' },
    T2: { color: 'YELLOW', label: 'DELAYED', tape: 'Yellow' },
    T3: { color: 'GREEN', label: 'MINOR', tape: 'Green' },
    T4: { color: 'BLUE', label: 'EXPECTANT', tape: 'Blue' },
    DEAD: { color: 'BLACK', label: 'DECEASED', tape: 'Black' }
  },
  
  nato_forms: {
    field_medical_card: 'STANAG 2132',
    casualty_feeder_card: 'STANAG 2133',
    patient_tracking: 'STANAG 2516'
  },
  
  electronic_tracking: {
    systems: ['TC3', 'TMIP-M', 'DCIPS'],
    barcode_wristband: true,
    gps_tracking: 'If available'
  }
};

// ============================================================================
// TRIAGE OFFICER PERFORMANCE MODELING
// ============================================================================

/**
 * Triage officer accuracy degradation under operational stress
 * 
 * DATA SOURCE: Ukraine 2022-2024 + NATO exercise observations
 * 
 * TEACHING POINT:
 * Triage accuracy is NOT a constant 95%. Fatigue, mass casualties, and
 * sustained operations dramatically degrade decision quality. Ukrainian
 * medical personnel reported making triage errors after 8+ hours of
 * continuous mass casualty processing. This is why rotation of triage
 * officers and having multiple trained personnel is essential.
 * 
 * UNDERTRIAGE (T1â†’T2): Patient dies who could have been saved
 * OVERTRIAGE (T3â†’T2): Wastes surgical resources, delays T1 patients
 */
export const TRIAGE_OFFICER_PERFORMANCE = {
  /**
   * OPTIMAL conditions: Fresh officer, <10 casualties/hour, adequate resources
   */
  OPTIMAL: {
    accuracy: 0.95,                 // 95% correct triage decisions
    undertriage_rate: 0.03,         // 3% T1 missed as T2
    overtriage_rate: 0.02,          // 2% T2/T3 overtriaged
    conditions: {
      officer_fatigue: 'FRESH',
      casualty_rate_per_hour: 10,
      hours_continuous: 2,
      resource_availability: 'ADEQUATE'
    }
  },

  /**
   * FATIGUED: >4 hours continuous triage without break
   */
  FATIGUED: {
    accuracy: 0.82,                 // 82% accuracy
    undertriage_rate: 0.10,         // 10% T1 missed (2x deaths)
    overtriage_rate: 0.08,          // 8% overtriage (resource waste)
    conditions: {
      officer_fatigue: 'FATIGUED',
      hours_continuous: 6,
      performance_degradation: 'MODERATE'
    },
    mitigation: {
      rest_breaks_hours: 2,         // Break every 2 hours
      rotation_available: true,     // Multiple trained officers
      stimulants_acceptable: true   // Caffeine to maintain alertness
    }
  },

  /**
   * MASS_CASUALTY: >20 casualties/hour (overwhelming pace)
   */
  MASS_CASUALTY: {
    accuracy: 0.70,                 // 70% accuracy
    undertriage_rate: 0.18,         // 18% T1 missed (unacceptable)
    overtriage_rate: 0.12,          // 12% overtriage
    conditions: {
      casualty_rate_per_hour: 30,
      decision_time_seconds: 45,    // Only 45s per patient
      resource_situation: 'CRITICAL'
    },
    response_needed: {
      additional_officers: 2,       // Need 3 total for parallel triage
      senior_oversight: true,       // Medical director validates decisions
      simplified_protocol: true,    // Use START triage (faster)
      accept_errors: true           // Some errors unavoidable
    }
  },

  /**
   * OVERWHELMED: >40 casualties/hour OR >12 hours continuous
   */
  OVERWHELMED: {
    accuracy: 0.55,                 // 55% accuracy (coin flip territory)
    undertriage_rate: 0.28,         // 28% T1 missed (disaster)
    overtriage_rate: 0.17,          // 17% overtriage
    conditions: {
      casualty_rate_per_hour: 50,
      hours_continuous: 14,
      officer_state: 'EXHAUSTED',
      decision_quality: 'SEVERELY_DEGRADED'
    },
    critical_situation: {
      officer_replacement_urgent: true,
      errors_causing_deaths: true,
      medical_system_breaking: true,
      need_additional_mtf: true     // Single MTF cannot handle load
    }
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate triage distribution for a given casualty count
 * 
 * @param {number} totalWIA - Total wounded in action
 * @param {string} operationType - 'ATTACK', 'DEFEND', or 'BALANCED'
 * @returns {Object} Casualties by triage category
 * 
 * @example
 * const casualties = calculateTriageDistribution(100, 'ATTACK');
 * // Returns: { T1: 28, T2: 38, T3: 29, T4: 5 }
 * 
 * TEACHING POINT:
 * Distribution changes based on operation type. Attacks produce MORE T1
 * casualties (28% vs 22% defense) because attacking force is exposed.
 * Understanding this helps plan surgical capacity before operations.
 */
export function calculateTriageDistribution(totalWIA, operationType = 'BALANCED') {
  const dist = TRIAGE_CATEGORIES;
  
  let t1_pct, t2_pct, t3_pct, t4_pct;
  
  if (operationType === 'ATTACK') {
    t1_pct = dist.T1_IMMEDIATE.attack_percentage;
    t2_pct = dist.T2_DELAYED.attack_percentage;
    t3_pct = dist.T3_MINIMAL.attack_percentage;
    t4_pct = dist.T4_EXPECTANT.baseline_percentage;
  } else if (operationType === 'DEFEND') {
    t1_pct = dist.T1_IMMEDIATE.defense_percentage;
    t2_pct = dist.T2_DELAYED.defense_percentage;
    t3_pct = dist.T3_MINIMAL.defense_percentage;
    t4_pct = dist.T4_EXPECTANT.baseline_percentage;
  } else {
    // BALANCED - use baseline percentages
    t1_pct = dist.T1_IMMEDIATE.baseline_percentage;
    t2_pct = dist.T2_DELAYED.baseline_percentage;
    t3_pct = dist.T3_MINIMAL.baseline_percentage;
    t4_pct = dist.T4_EXPECTANT.baseline_percentage;
  }
  
  return {
    T1: Math.round(totalWIA * t1_pct),
    T2: Math.round(totalWIA * t2_pct),
    T3: Math.round(totalWIA * t3_pct),
    T4: Math.round(totalWIA * t4_pct)
  };
}

/**
 * Calculate T1 survival probability based on time to surgery
 * 
 * @param {number} timeToSurgeryMinutes - Minutes from injury to surgery start
 * @returns {number} Survival probability (0.0 to 1.0)
 * 
 * @example
 * const survival = calculateT1Survival(45);  // Returns 0.92 (92%)
 * const survival = calculateT1Survival(180); // Returns 0.35 (35%)
 * 
 * TEACHING POINT:
 * This is the "golden hour" concept quantified. Survival drops from 92% to
 * 68% to 35% as time increases. This is WHY forward surgical capability
 * (Role 2 Enhanced) is so critical - it brings surgery closer to injury.
 */
export function calculateT1Survival(timeToSurgeryMinutes) {
  const curves = TRIAGE_CATEGORIES.T1_IMMEDIATE.survival_curves;
  
  if (timeToSurgeryMinutes < 60) {
    return curves.under_60min;
  } else if (timeToSurgeryMinutes < 120) {
    return curves.between_60_120min;
  } else if (timeToSurgeryMinutes < 240) {
    return curves.between_120_240min;
  } else {
    return curves.over_240min;
  }
}

/**
 * Calculate T2 survival probability based on time to surgery
 * 
 * @param {number} timeToSurgeryHours - Hours from injury to surgery start
 * @returns {number} Survival probability (0.0 to 1.0)
 * 
 * @example
 * const survival = calculateT2Survival(4);  // Returns 0.96 (96%)
 * const survival = calculateT2Survival(36); // Returns 0.87 (87%)
 * 
 * TEACHING POINT:
 * T2 patients are more forgiving of delays. 96% survive even if surgery
 * delayed 6 hours. This allows medical regulating - moving T2 patients to
 * less busy MTFs while keeping T1 at nearest facility.
 */
export function calculateT2Survival(timeToSurgeryHours) {
  const curves = TRIAGE_CATEGORIES.T2_DELAYED.survival_curves;
  
  if (timeToSurgeryHours < 6) {
    return curves.under_6hours;
  } else if (timeToSurgeryHours < 24) {
    return curves.between_6_24hours;
  } else if (timeToSurgeryHours < 72) {
    return curves.between_24_72hours;
  } else {
    return curves.over_72hours;
  }
}

/**
 * Calculate blood product requirements for a casualty mix
 * 
 * @param {Object} triageDistribution - Output from calculateTriageDistribution()
 * @returns {Object} Total blood product units needed
 * 
 * @example
 * const blood = calculateBloodRequirements({ T1: 28, T2: 38, T3: 29, T4: 5 });
 * // Returns: { prbc: 488, ffp: 412, platelets: 56 }
 * 
 * TEACHING POINT:
 * Blood is the most critical supply in military medicine. PRBC shelf life
 * is only 35-42 days refrigerated. This calculation helps determine if
 * current blood stocks can support expected casualties. If not, must either:
 * 1) Request emergency resupply, 2) Activate walking blood bank, or
 * 3) Limit surgical operations to save blood for most critical cases.
 */
export function calculateBloodRequirements(triageDistribution) {
  const t1_blood = TRIAGE_CATEGORIES.T1_IMMEDIATE.resources.blood_products;
  const t2_blood = TRIAGE_CATEGORIES.T2_DELAYED.resources.blood_products;
  
  const total_prbc = (triageDistribution.T1 * t1_blood.prbc_units) +
                     (triageDistribution.T2 * t2_blood.prbc_units);
                     
  const total_ffp = (triageDistribution.T1 * t1_blood.ffp_units) +
                    (triageDistribution.T2 * t2_blood.ffp_units);
                    
  const total_platelets = (triageDistribution.T1 * t1_blood.platelet_units) +
                          (triageDistribution.T2 * t2_blood.platelet_units);
  
  return {
    prbc: total_prbc,
    ffp: total_ffp,
    platelets: total_platelets,
    units_total: total_prbc + total_ffp + total_platelets
  };
}

/**
 * Calculate surgical capacity required for casualty load
 * 
 * @param {Object} triageDistribution - Output from calculateTriageDistribution()
 * @param {number} timePeriodHours - Time period to spread surgeries over
 * @returns {Object} Surgical team requirements
 * 
 * @example
 * const surgical = calculateSurgicalCapacity({ T1: 28, T2: 38, T3: 29, T4: 5 }, 24);
 * // Returns: { teams_needed: 4, total_hours: 196, utilization: 82% }
 * 
 * TEACHING POINT:
 * Surgical capacity is THE bottleneck in military medicine. One surgical team
 * can do 4-6 major surgeries per 24 hours. If you have 28 T1 casualties and
 * 2 surgical teams, you can only treat ~12 in first 24 hours. The other 16
 * will deteriorate while waiting. This is why medical planners must ensure
 * adequate surgical teams BEFORE operations begin.
 */
export function calculateSurgicalCapacity(triageDistribution, timePeriodHours = 24) {
  const t1_time = TRIAGE_CATEGORIES.T1_IMMEDIATE.resources.surgery_duration_minutes;
  const t2_time = TRIAGE_CATEGORIES.T2_DELAYED.resources.surgery_duration_minutes;
  
  const total_surgery_minutes = (triageDistribution.T1 * t1_time) +
                                (triageDistribution.T2 * t2_time);
  
  const total_surgery_hours = total_surgery_minutes / 60;
  
  // One surgical team works maximum 18 hours per 24 (need rest/breaks)
  const effective_hours_per_team = timePeriodHours * 0.75;
  
  const teams_needed = Math.ceil(total_surgery_hours / effective_hours_per_team);
  
  const utilization = (total_surgery_hours / (teams_needed * effective_hours_per_team));
  
  return {
    teams_needed: teams_needed,
    total_surgery_hours: total_surgery_hours,
    utilization_percentage: Math.round(utilization * 100),
    warning: teams_needed > 4 ? 'EXCEEDS_TYPICAL_BRIGADE_CAPACITY' : null
  };
}

/**
 * Adjust triage categories based on mass casualty situation
 * 
 * @param {Object} triageDistribution - Initial triage distribution
 * @param {boolean} isMasscasualty - True if MASCAL declared
 * @returns {Object} Adjusted triage distribution (some T1â†’T4 conversion)
 * 
 * @example
 * const adjusted = adjustForMassCalculty({ T1: 40, T2: 60, T3: 45, T4: 5 }, true);
 * // Returns: { T1: 32, T2: 60, T3: 45, T4: 13 } - 8 T1 patients reclassified T4
 * 
 * TEACHING POINT:
 * This is the hardest function in the entire system. During MASCAL, some
 * patients who WOULD survive with treatment (T1) are reclassified as T4
 * (Expectant) to save resources for patients with better survival probability.
 * This is triage in its purest, most brutal form. Medical personnel must be
 * trained for this decision BEFORE it happens in combat.
 */
export function adjustForMassCapacity(triageDistribution, isMassCalculty) {
  if (!isMassCalculty) {
    return triageDistribution;
  }
  
  // In MASCAL, most critically injured T1 patients (worst 20%) become T4
  const t1_to_t4_conversion_rate = 0.20;
  
  const t1_converted = Math.floor(triageDistribution.T1 * t1_to_t4_conversion_rate);
  
  return {
    T1: triageDistribution.T1 - t1_converted,
    T2: triageDistribution.T2, // T2 unchanged
    T3: triageDistribution.T3, // T3 unchanged
    T4: triageDistribution.T4 + t1_converted,
    converted_t1_to_t4: t1_converted,
    reason: 'MASCAL_RESOURCE_ALLOCATION'
  };
}

/**
 * Calculate triage officer accuracy based on operational conditions
 * 
 * @param {number} hoursContinuous - Hours triaging without break
 * @param {number} casualtyRatePerHour - Casualties being triaged per hour
 * @returns {Object} Accuracy metrics and recommendations
 * 
 * @example
 * const accuracy = getTriageAccuracy(8, 35);
 * // Returns: { accuracy: 0.70, condition: 'MASS_CASUALTY', ... }
 * 
 * TEACHING POINT:
 * Triage accuracy is NOT constant. After 8+ hours or with >30 casualties/hour,
 * accuracy drops to 70% or worse. This means 30% of triage decisions are WRONG.
 * Officers must be rotated, breaks must be enforced, and senior oversight is
 * essential during sustained operations.
 */
export function getTriageAccuracy(hoursContinuous, casualtyRatePerHour) {
  const perf = TRIAGE_OFFICER_PERFORMANCE;
  
  // Determine condition based on inputs
  if (casualtyRatePerHour > 40 || hoursContinuous > 12) {
    return {
      ...perf.OVERWHELMED,
      condition: 'OVERWHELMED',
      recommendation: 'REPLACE_OFFICER_IMMEDIATELY'
    };
  } else if (casualtyRatePerHour > 20) {
    return {
      ...perf.MASS_CASUALTY,
      condition: 'MASS_CASUALTY',
      recommendation: 'ADD_ADDITIONAL_OFFICERS'
    };
  } else if (hoursContinuous > 4) {
    return {
      ...perf.FATIGUED,
      condition: 'FATIGUED',
      recommendation: 'ROTATE_OFFICER_SOON'
    };
  } else {
    return {
      ...perf.OPTIMAL,
      condition: 'OPTIMAL',
      recommendation: 'CONTINUE_OPERATIONS'
    };
  }
}

/**
 * Calculate Return to Duty (RTD) projections for T3 casualties
 * 
 * @param {number} t3Count - Number of T3 casualties
 * @param {number} daysElapsed - Days since injury
 * @returns {Object} RTD timeline projections
 * 
 * @example
 * const rtd = calculateReturnToDuty(50, 3);
 * // Returns: { rtd_so_far: 22, still_sick: 28, rtd_by_7days: 35 }
 * 
 * TEACHING POINT:
 * T3 casualties are critical for maintaining unit combat power. 70% return to
 * duty within 7 days if properly treated. This is why Role 1 facilities focus
 * on T3 treatment - keeping soldiers with their units preserves unit cohesion
 * and reduces evacuation burden. In Ukrainian operations, many T3 patients
 * remained with units rather than evacuating, preserving evacuation assets
 * for T1/T2 casualties.
 */
export function calculateReturnToDuty(t3Count, daysElapsed) {
  const rtd = TRIAGE_CATEGORIES.T3_MINIMAL.rtd_rates;
  
  let rtd_so_far = 0;
  
  if (daysElapsed >= 1) {
    rtd_so_far += Math.round(t3Count * rtd.within_24hours);
  }
  if (daysElapsed >= 3) {
    rtd_so_far += Math.round(t3Count * (rtd.within_72hours - rtd.within_24hours));
  }
  if (daysElapsed >= 7) {
    rtd_so_far += Math.round(t3Count * (rtd.within_7days - rtd.within_72hours));
  }
  
  const still_sick = t3Count - rtd_so_far;
  const evacuated = Math.round(t3Count * rtd.medical_evacuation);
  
  return {
    total_t3: t3Count,
    rtd_so_far: rtd_so_far,
    still_sick: still_sick,
    will_evacuate: evacuated,
    rtd_by_7days: Math.round(t3Count * rtd.within_7days),
    rtd_percentage: Math.round((rtd_so_far / t3Count) * 100)
  };
}

// ============================================================================
// EDUCATIONAL METADATA
// ============================================================================

/**
 * Learning objectives for medical students
 */
export const LEARNING_OBJECTIVES = {
  primary: [
    "Understand NATO 5-category triage system (T1-T4)",
    "Calculate survival probabilities based on time to treatment",
    "Quantify resource requirements (blood, surgical capacity)",
    "Recognize triage officer fatigue effects on accuracy",
    "Apply MASCAL triage protocols (T1â†’T4 conversion)"
  ],
  
  secondary: [
    "Differentiate between medical and resource-driven triage",
    "Plan blood product inventory for expected casualties",
    "Calculate surgical team capacity vs casualty load",
    "Understand T3 RTD importance for unit strength",
    "Recognize when mass casualty protocols needed"
  ],
  
  romanian_context: [
    "CONTESTED environment most likely (Eastern European conflict)",
    "Golden hour rarely achieved (38% vs 75% doctrine)",
    "T2 and PFC capabilities critical for success",
    "Walking blood bank as backup to supply chain",
    "Winter hypothermia significant in Carpathian operations"
  ]
};

// ============================================================================
// EXPORTS
// ============================================================================

// Named default export (fixes lint warning)
const triageCategoriesConstants = {
  TRIAGE_CATEGORIES,
  TRIAGE_OFFICER_PERFORMANCE,
  START_TRIAGE_ALGORITHM,
  SALT_TRIAGE_ALGORITHM,
  RE_TRIAGE_PROTOCOL,
  CBRN_TRIAGE_MODIFICATIONS,
  BURN_TRIAGE_CRITERIA,
  PEDIATRIC_TRIAGE_MODIFICATIONS,
  TRIAGE_DOCUMENTATION,
  calculateTriageDistribution,
  calculateT1Survival,
  calculateT2Survival,
  calculateBloodRequirements,
  calculateSurgicalCapacity,
  adjustForMassCapacity,
  getTriageAccuracy,
  calculateReturnToDuty,
  LEARNING_OBJECTIVES
};

export default triageCategoriesConstants;
